import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LiveKitRoom, useLocalParticipant, useTracks, RoomAudioRenderer,
  useParticipants, useRoomContext,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track, RoomEvent } from 'livekit-client';
import { useAuth } from '../contexts/AuthContext';
import { Mic, MicOff, Video, VideoOff, MonitorUp, PhoneOff, Users, Copy,
  Loader2, AlertCircle, CheckCheck, User, ScreenShare, ScreenShareOff, UserX, BellRing } from 'lucide-react';
import { toast } from 'sonner';

const fmtDuration = (s) => { if (!s) return '0s'; const m = Math.floor(s/60); const r = s%60; return m ? `${m}m ${r}s` : `${r}s`; };
const circleBtn = (bg, size=52) => ({ width: size, height: size, borderRadius: '50%', border: 'none', background: bg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 });

function VideoTile({ track, label, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !track?.publication?.track) return;
    track.publication.track.attach(el);
    return () => { try { track.publication.track.detach(el); } catch {} };
  }, [track]);
  return (
    <div style={{ position: 'relative', background: '#1a1a1a', borderRadius: 8, overflow: 'hidden', ...style }}>
      <video ref={ref} autoPlay playsInline muted={track?.participant?.isLocal}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      {label && <div style={{ position: 'absolute', bottom: 6, left: 8, color: '#fff', fontSize: 12, textShadow: '0 1px 4px rgba(0,0,0,0.9)', fontWeight: 500, background: 'rgba(0,0,0,0.4)', padding: '2px 6px', borderRadius: 4 }}>{label}</div>}
    </div>
  );
}

function ScreenTile({ track }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !track?.publication?.track) return;
    track.publication.track.attach(el);
    return () => { try { track.publication.track.detach(el); } catch {} };
  }, [track]);
  return <video ref={ref} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000' }} />;
}

function ParticipantsPanel({ onClose, meetingId, api, knockRequests = [], handleKnockResponse }) {
  const participants = useParticipants();

  const muteMic = async (p) => {
    const pub = p.getTrackPublication(Track.Source.Microphone);
    if (pub && !pub.isMuted) {
      try {
        await api.post(`/jitsi-meetings/${meetingId}/mute-participant`, {
          identity: p.identity, trackSid: pub.trackSid, muted: true,
        });
        toast.success(`${p.name}'s mic muted`);
      } catch { toast.error('Failed to mute mic'); }
    } else {
      try {
        await api.post(`/jitsi-meetings/${meetingId}/request-unmute`, { identity: p.identity, kind: 'mic' });
        toast.info(`Asked ${p.name} to unmute their mic`);
      } catch { toast.error('Failed to send request'); }
    }
  };

  const muteCam = async (p) => {
    const pub = p.getTrackPublication(Track.Source.Camera);
    if (pub && !pub.isMuted) {
      try {
        await api.post(`/jitsi-meetings/${meetingId}/mute-participant`, {
          identity: p.identity, trackSid: pub.trackSid, muted: true,
        });
        toast.success(`${p.name}'s camera disabled`);
      } catch { toast.error('Failed to disable camera'); }
    } else {
      try {
        await api.post(`/jitsi-meetings/${meetingId}/request-unmute`, { identity: p.identity, kind: 'camera' });
        toast.info(`Asked ${p.name} to turn on their camera`);
      } catch { toast.error('Failed to send request'); }
    }
  };

  const removeGuest = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the meeting?`)) return;
    try {
      await api.post(`/jitsi-meetings/${meetingId}/remove-participant`, { identity: p.identity });
      toast.success(`${p.name} removed from meeting`);
    } catch { toast.error('Failed to remove participant'); }
  };

  const toggleScreenSharePermission = async (p) => {
    const currentlyAllowed = p.attributes?.screenShareAllowed === 'true';
    try {
      await api.post(`/jitsi-meetings/${meetingId}/screen-share-permission`, {
        identity: p.identity, allowed: !currentlyAllowed,
      });
      toast.success(`Screen share ${!currentlyAllowed ? 'allowed for' : 'revoked from'} ${p.name}`);
    } catch { toast.error('Failed to update screen share permission'); }
  };

  return (
    <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 280, background: '#1a1a1a', borderLeft: '1px solid #333', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Participants ({participants.length})</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
        {knockRequests.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ color: '#a78bfa', fontSize: 11, fontWeight: 600, padding: '4px 6px 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Waiting to join</div>
            {knockRequests.map(req => (
              <div key={req.guestIdentity} style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, padding: '10px', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <User size={14} color="#a78bfa" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.guestName}</div>
                    <div style={{ color: '#888', fontSize: 11 }}>wants to join</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleKnockResponse(req, false)} style={{ flex: 1, background: '#3c3c3c', border: 'none', borderRadius: 6, color: '#ccc', padding: '6px 0', cursor: 'pointer', fontSize: 12 }}>Deny</button>
                  <button onClick={() => handleKnockResponse(req, true)} style={{ flex: 1, background: '#7c3aed', border: 'none', borderRadius: 6, color: '#fff', padding: '6px 0', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Admit</button>
                </div>
              </div>
            ))}
            <div style={{ borderBottom: '1px solid #333', marginBottom: 8 }} />
          </div>
        )}
        {participants.map(p => {
          const micPub = p.getTrackPublication(Track.Source.Microphone);
          const camPub = p.getTrackPublication(Track.Source.Camera);
          const micOn = micPub && !micPub.isMuted;
          const camOn = camPub && !camPub.isMuted;
          const screenAllowed = p.attributes?.screenShareAllowed === 'true';
          return (
            <div key={p.sid} style={{ padding: '8px 6px', borderRadius: 6, marginBottom: 4, background: p.isLocal ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <User size={16} color="#aaa" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name || p.identity} {p.isLocal && <span style={{ color: '#888', fontSize: 11 }}>(You)</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                    {micOn ? <Mic size={11} color="#4ade80" /> : <MicOff size={11} color="#dc2626" />}
                    {camOn ? <Video size={11} color="#4ade80" /> : <VideoOff size={11} color="#dc2626" />}
                  </div>
                </div>
              </div>
              {!p.isLocal && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => muteMic(p)} title={micOn ? 'Mute mic' : 'Ask to unmute mic'}
                    style={{ background: micOn ? 'rgba(74,222,128,0.15)' : 'rgba(124,58,237,0.15)', border: 'none', borderRadius: 5, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {micOn ? <Mic size={13} color="#4ade80" /> : <BellRing size={13} color="#a78bfa" />}
                    <span style={{ fontSize: 11, color: micOn ? '#4ade80' : '#a78bfa' }}>{micOn ? 'Mute' : 'Ask unmute'}</span>
                  </button>
                  <button onClick={() => muteCam(p)} title={camOn ? 'Disable camera' : 'Ask to enable camera'}
                    style={{ background: camOn ? 'rgba(74,222,128,0.15)' : 'rgba(124,58,237,0.15)', border: 'none', borderRadius: 5, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {camOn ? <Video size={13} color="#4ade80" /> : <BellRing size={13} color="#a78bfa" />}
                    <span style={{ fontSize: 11, color: camOn ? '#4ade80' : '#a78bfa' }}>{camOn ? 'Cam off' : 'Ask cam'}</span>
                  </button>
                  <button onClick={() => toggleScreenSharePermission(p)} title="Toggle screen share permission"
                    style={{ background: screenAllowed ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.08)', border: screenAllowed ? '1px solid #7c3aed' : '1px solid #444', borderRadius: 5, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {screenAllowed ? <ScreenShare size={13} color="#a78bfa" /> : <ScreenShareOff size={13} color="#888" />}
                    <span style={{ fontSize: 11, color: screenAllowed ? '#a78bfa' : '#888' }}>{screenAllowed ? 'Can share' : 'Allow share'}</span>
                  </button>
                  <button onClick={() => removeGuest(p)} title="Remove from meeting"
                    style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid #dc2626', borderRadius: 5, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <UserX size={13} color="#f87171" />
                    <span style={{ fontSize: 11, color: '#f87171' }}>Remove</span>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MeetRoom({ meetingId, meeting, onLeave, api }) {
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedSeconds, setRecordedSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [knockRequests, setKnockRequests] = useState([]); // [{guestName, guestIdentity, meetingId}]
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const tickerRef = useRef(null);
  const room = useRoomContext();

  const cameraTracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const screenTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: false });
  const hasScreen = screenTracks.length > 0;

  useEffect(() => {
    if (recording) { tickerRef.current = setInterval(() => setRecordedSeconds(s => s+1), 1000); }
    else clearInterval(tickerRef.current);
    return () => clearInterval(tickerRef.current);
  }, [recording]);

  // Listen for guest knock requests
  useEffect(() => {
    const onData = (payload) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg.type === 'knock_request') {
          setKnockRequests(prev => {
            if (prev.find(r => r.guestIdentity === msg.guestIdentity)) return prev;
            return [...prev, { guestName: msg.guestName, guestIdentity: msg.guestIdentity, meetingId: msg.meetingId }];
          });
          setShowParticipants(true);
        }
      } catch {}
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => room.off(RoomEvent.DataReceived, onData);
  }, [room]);

  const handleKnockResponse = useCallback(async (req, approved) => {
    try {
      await fetch(`https://lms.servall.in/guest/meeting/${req.meetingId || meetingId}/knock-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestIdentity: req.guestIdentity, approved }),
      });
      setKnockRequests(prev => prev.filter(r => r.guestIdentity !== req.guestIdentity));
      toast[approved ? 'success' : 'info'](`${req.guestName} ${approved ? 'admitted' : 'denied'}`);
    } catch { toast.error('Failed to respond'); }
  }, [meetingId]);

  const toggleMic = useCallback(async () => { await localParticipant.setMicrophoneEnabled(!micOn); setMicOn(v => !v); }, [localParticipant, micOn]);
  const toggleCam = useCallback(async () => { await localParticipant.setCameraEnabled(!camOn); setCamOn(v => !v); }, [localParticipant, camOn]);
  const toggleScreen = useCallback(async () => {
    if (!screenSharing) {
      try { await localParticipant.setScreenShareEnabled(true); setScreenSharing(true); toast.success('Screen sharing started'); }
      catch { toast.error('Screen share cancelled'); }
    } else { await localParticipant.setScreenShareEnabled(false); setScreenSharing(false); toast.info('Screen sharing stopped'); }
  }, [localParticipant, screenSharing]);

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(`${window.location.origin}/guest/meeting/${meetingId}`);
    setCopied(true); toast.success('Guest link copied!'); setTimeout(() => setCopied(false), 3000);
  }, [meetingId]);

  const handleRecord = useCallback(async () => {
    if (recording) { if (mediaRecorderRef.current) mediaRecorderRef.current.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      audioStreamRef.current = stream;
      const mime = ['audio/webm;codecs=opus','audio/webm','audio/ogg'].find(m => MediaRecorder.isTypeSupported(m)) || '';
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = async (e) => {
        if (e.data && e.data.size > 50*1024) {
          const ext = (mime||'').includes('ogg') ? '.ogg' : '.webm';
          const fd = new FormData();
          fd.append('file', new Blob([e.data], { type: mime||'audio/webm' }), `meeting-${meetingId}${ext}`);
          fd.append('autoProcess','true');
          setUploading(true);
          try { await api.post(`/jitsi-meetings/${meetingId}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('AI summary queued!'); }
          catch { toast.error('Upload failed'); } finally { setUploading(false); }
        }
      };
      mr.onstop = () => { audioStreamRef.current?.getTracks().forEach(t => t.stop()); audioStreamRef.current = null; };
      mr.start(30000); setRecording(true); setRecordedSeconds(0); toast.success('Recording started');
    } catch { toast.error('Mic access needed'); }
  }, [recording, meetingId, api]);

  return (
    <div style={{ position: 'relative', height: '100%', background: '#111', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px 16px', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, borderBottom: '1px solid #2a2a2a' }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{meeting?.title}</div>
          <div style={{ color: '#888', fontSize: 12 }}>Host: {meeting?.hostName}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={copyLink} style={{ background: '#2a2a2a', border: 'none', borderRadius: 20, color: '#fff', padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            {copied ? <CheckCheck size={13} color="#4ade80" /> : <Copy size={13} />} Invite
          </button>
          <button onClick={() => setShowParticipants(v => !v)} style={{ background: showParticipants ? '#3c3c3c' : '#2a2a2a', border: 'none', borderRadius: 20, color: '#fff', padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <Users size={13} /> {participants.length}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', padding: 6, display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
        {hasScreen ? (
          <>
            <div style={{ flex: 1, borderRadius: 8, overflow: 'hidden', minHeight: 0 }}>
              <ScreenTile track={screenTracks[0]} />
            </div>
            <div style={{ height: 90, display: 'flex', gap: 6, flexShrink: 0 }}>
              {cameraTracks.map((t, i) => <VideoTile key={i} track={t} label={t.participant?.name} style={{ flex: 1, height: 90 }} />)}
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'grid', gap: 6, minHeight: 0,
            gridTemplateColumns: cameraTracks.length <= 1 ? '1fr' : cameraTracks.length <= 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)' }}>
            {cameraTracks.map((t, i) => <VideoTile key={i} track={t} label={t.participant?.name} style={{ minHeight: 0 }} />)}
          </div>
        )}
        {showParticipants && <ParticipantsPanel onClose={() => setShowParticipants(false)} meetingId={meetingId} api={api} knockRequests={knockRequests} handleKnockResponse={handleKnockResponse} />}
      </div>

      <RoomAudioRenderer />

      {/* Knock requests notification stack */}
      {knockRequests.length > 0 && (
        <div style={{ position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 250, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 300, maxWidth: 380 }}>
          {knockRequests.map(req => (
            <div key={req.guestIdentity} style={{ background: '#1e1e2e', border: '1px solid #7c3aed', borderRadius: 12, padding: '14px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <User size={18} color="#a78bfa" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.guestName}</div>
                <div style={{ color: '#888', fontSize: 11 }}>wants to join the meeting</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => handleKnockResponse(req, false)} style={{ background: '#3c3c3c', border: 'none', borderRadius: 6, color: '#ccc', padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>Deny</button>
                <button onClick={() => handleKnockResponse(req, true)} style={{ background: '#7c3aed', border: 'none', borderRadius: 6, color: '#fff', padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Admit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: '14px 20px 18px', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexShrink: 0, borderTop: '1px solid #2a2a2a', flexWrap: 'wrap' }}>
        <button style={circleBtn(micOn ? '#3c3c3c' : '#dc2626')} onClick={toggleMic} title={micOn ? 'Mute' : 'Unmute'}>
          {micOn ? <Mic size={22} color="#fff" /> : <MicOff size={22} color="#fff" />}
        </button>
        <button style={circleBtn(camOn ? '#3c3c3c' : '#dc2626')} onClick={toggleCam} title={camOn ? 'Camera off' : 'Camera on'}>
          {camOn ? <Video size={22} color="#fff" /> : <VideoOff size={22} color="#fff" />}
        </button>
        <button style={circleBtn(screenSharing ? '#1a7f4b' : '#3c3c3c')} onClick={toggleScreen} title="Share screen">
          <MonitorUp size={22} color="#fff" />
        </button>
        {recording ? (
          <button onClick={handleRecord} style={{ height: 52, background: '#dc2626', border: 'none', borderRadius: 26, color: '#fff', padding: '0 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
            <MicOff size={18} /> Stop REC {fmtDuration(recordedSeconds)}
          </button>
        ) : uploading ? (
          <div style={{ height: 52, background: '#3c3c3c', borderRadius: 26, color: '#aaa', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Uploading...
          </div>
        ) : (
          <button onClick={handleRecord} style={{ height: 52, background: '#7c3aed', border: 'none', borderRadius: 26, color: '#fff', padding: '0 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
            <Mic size={18} /> AI Summary
          </button>
        )}
        <button style={circleBtn('#dc2626')} onClick={onLeave} title="Leave">
          <PhoneOff size={22} color="#fff" />
        </button>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const JitsiRoomPage = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [token, setToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [meetRes, tokenRes] = await Promise.all([
          api.get(`/jitsi-meetings/${meetingId}`),
          api.get(`/jitsi-meetings/token/${meetingId}`),
        ]);
        setMeeting(meetRes.data);
        setToken(tokenRes.data.token);
        setLivekitUrl(tokenRes.data.livekitUrl);
        await api.post(`/jitsi-meetings/${meetingId}/status`, { status: 'started' });
      } catch { setError('Meeting not found or access denied'); }
      finally { setLoading(false); }
    })();
  }, [api, meetingId]);

  const handleLeave = async () => {
    try { await api.post(`/jitsi-meetings/${meetingId}/status`, { status: 'ended' }); } catch {}
    navigate('/lms-meet');
  };

  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <Loader2 size={40} color="#fff" style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto 12px' }} />
        <p style={{ color: '#888' }}>Joining meeting...</p>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ position: 'fixed', inset: 0, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: 32 }}>
        <AlertCircle size={48} color="#dc2626" style={{ display: 'block', margin: '0 auto 16px' }} />
        <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 8 }}>Cannot Join Meeting</h2>
        <p style={{ color: '#888', marginBottom: 24 }}>{error}</p>
        <button onClick={() => navigate('/lms-meet')} style={{ background: '#333', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer' }}>Back</button>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: '#111' }}>
      {token && livekitUrl && (
        <LiveKitRoom token={token} serverUrl={livekitUrl} connect={true} video={true} audio={true} onDisconnected={handleLeave} style={{ height: '100%' }}>
          <MeetRoom meetingId={meetingId} meeting={meeting} onLeave={handleLeave} api={api} />
        </LiveKitRoom>
      )}
    </div>
  );
};

export default JitsiRoomPage;
